import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabaseClient";

export type SignatureBookAddonMode = "none" | "paid" | "gift" | "promo";

export interface SignatureBookAddonState {
  mode: SignatureBookAddonMode;
  amount: number;
  promoCode: string;
  alreadyActive: boolean;
}

interface Props {
  tenantId: string;
  clientId: string;
  contractId?: string;
  value: SignatureBookAddonState;
  onChange: (val: SignatureBookAddonState) => void;
}

export const BLANK_SIGNATURE_BOOK_ADDON: SignatureBookAddonState = {
  mode: "none",
  amount: 80,
  promoCode: "",
  alreadyActive: false,
};

export default function SignatureBookAddonSection({
  tenantId,
  clientId,
  contractId,
  value,
  onChange,
}: Props) {
  const [checking, setChecking] = useState(false);

  /* ── Check if client already has an active signature_book addon ── */
  useEffect(() => {
    if (!tenantId || !clientId) return;
    const check = async () => {
      setChecking(true);
      try {
        const { data: addonRow } = await supabase
          .from("client_addons")
          .select("id, status")
          .eq("tenant_id", tenantId)
          .eq("client_id", clientId)
          .eq("addon_key", "signature_book")
          .maybeSingle();

        const alreadyActive = addonRow?.status === "active";
        onChange({ ...value, alreadyActive, mode: alreadyActive ? "none" : value.mode });
      } finally {
        setChecking(false);
      }
    };
    check();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, clientId]);

  const set = (patch: Partial<SignatureBookAddonState>) => onChange({ ...value, ...patch });

  if (checking) {
    return <div className="h-6 w-48 bg-slate-100 rounded animate-pulse" />;
  }

  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-amber-50/60 border-b border-slate-100">
        <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-100">
          <i className="ri-book-open-line text-amber-600 text-sm"></i>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[#0f172a] text-sm font-semibold">Libro de Firmas</p>
          <p className="text-slate-400 text-xs">Add-on de libro digital con mensajes sobre fotos</p>
        </div>
        {value.alreadyActive ? (
          <span className="flex items-center gap-1.5 text-xs font-medium text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full">
            <i className="ri-checkbox-circle-fill text-xs"></i>
            Ya activo
          </span>
        ) : (
          <span className="text-xs text-slate-400">No activado</span>
        )}
      </div>

      {/* Already active info */}
      {value.alreadyActive ? (
        <div className="px-4 py-3 flex items-center gap-2 text-xs text-slate-500">
          <i className="ri-information-line text-amber-500"></i>
          Este cliente ya tiene el Libro de Firmas activo. No se generará un cargo adicional.
          {contractId && (
            <a
              href={`/admin/signature-books`}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-auto text-amber-600 hover:text-amber-700 font-medium underline whitespace-nowrap"
            >
              Ver libros →
            </a>
          )}
        </div>
      ) : (
        <div className="px-4 py-4 space-y-4">
          {/* Mode selector */}
          <div>
            <p className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-2">Tipo de activación</p>
            <div className="grid grid-cols-3 gap-2">
              {([
                { id: "none", label: "No incluir", icon: "ri-close-circle-line", desc: "Sin libro" },
                { id: "paid", label: "Cobrado",    icon: "ri-money-dollar-circle-line", desc: `$${value.amount}` },
                { id: "gift", label: "Regalo",     icon: "ri-gift-line",          desc: "Sin costo" },
              ] as const).map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => set({ mode: opt.id })}
                  className={`flex flex-col items-center gap-1 p-3 rounded-lg border text-xs font-medium transition-all cursor-pointer ${
                    value.mode === opt.id
                      ? "border-amber-400 bg-amber-50 text-amber-700"
                      : "border-slate-200 text-slate-400 hover:border-slate-300 hover:text-slate-600"
                  }`}
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className={`${opt.icon} text-base`}></i>
                  </span>
                  <span>{opt.label}</span>
                  <span className={`text-[10px] ${value.mode === opt.id ? "text-amber-500" : "text-slate-300"}`}>{opt.desc}</span>
                </button>
              ))}
            </div>

            {/* Promo code row */}
            <button
              type="button"
              onClick={() => set({ mode: "promo" })}
              className={`mt-2 w-full flex items-center gap-2 p-2.5 rounded-lg border text-xs font-medium transition-all cursor-pointer ${
                value.mode === "promo"
                  ? "border-amber-400 bg-amber-50 text-amber-700"
                  : "border-slate-200 text-slate-400 hover:border-slate-300 hover:text-slate-600"
              }`}
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-coupon-3-line text-base"></i>
              </span>
              <span>Código Promocional</span>
              {value.mode === "promo" && (
                <span className="ml-auto text-[10px] text-amber-500">Introducir código abajo</span>
              )}
            </button>
          </div>

          {/* Paid — amount input */}
          {value.mode === "paid" && (
            <div>
              <label className="block text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5">
                Monto del cargo (USD)
              </label>
              <div className="flex items-center gap-2">
                <span className="text-slate-400 text-sm">$</span>
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={value.amount}
                  onChange={(e) => set({ amount: Number(e.target.value) })}
                  className="w-28 border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0f172a] outline-none focus:border-amber-400/50 transition-colors"
                />
              </div>
              <p className="text-slate-400 text-xs mt-1.5 flex items-center gap-1">
                <i className="ri-information-line"></i>
                Se creará un cargo <strong>Signature Book Activation</strong> en los cargos del contrato.
              </p>
            </div>
          )}

          {/* Promo code input */}
          {value.mode === "promo" && (
            <div>
              <label className="block text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5">
                Código Promocional
              </label>
              <input
                type="text"
                value={value.promoCode}
                onChange={(e) => set({ promoCode: e.target.value.toUpperCase() })}
                placeholder="LIBRO2025"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-amber-400/50 transition-colors font-mono uppercase"
              />
              <p className="text-slate-400 text-xs mt-1.5">El código queda registrado para referencia interna.</p>
            </div>
          )}

          {/* Gift info */}
          {value.mode === "gift" && (
            <div className="flex items-start gap-2 bg-emerald-50 rounded-lg px-3 py-2.5 text-xs text-emerald-700">
              <i className="ri-gift-line mt-0.5 shrink-0"></i>
              <span>El Libro de Firmas será activado sin costo y se registrará como regalo. Se creará automáticamente con guest_book habilitado.</span>
            </div>
          )}

          {/* Summary */}
          {value.mode !== "none" && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2.5">
              <i className="ri-checkbox-circle-fill text-amber-500 text-sm shrink-0 mt-0.5"></i>
              <p className="text-xs text-slate-600">
                {value.mode === "paid" && `Se activará el libro y se agregará un cargo de $${value.amount} al contrato. El libro quedará en borrador listo para configurar fotos.`}
                {value.mode === "gift" && "Se activará el libro como obsequio. Activa automáticamente el guest_book y expira en 3 meses."}
                {value.mode === "promo" && `Se activará con código "${value.promoCode || "…"}". Sin cargo adicional. Expira en 3 meses.`}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}